import React, { useState, useRef, useContext, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import saveIcon from "../../assets/save_icon.png";
import SectionDivider from "../../components/SectionDivider";
import Loader from "../../components/Loader";
import AreYouSureModal from "../../components/AreYouSureModal";
import ProductOrderList from "./purchases/ProductOrderList";
import SelectedProductOrderList from "./purchases/SelectedProductOrderList";
import InformationBox from "../../components/InformationBox";
import axiosClient from "../../services/axiosClient";
import PrintableSupplieUniform from "./PrintableSupplieUniform";
import { UserAuth } from "../../utils/AuthContext";

function findOutOfStockUniforms(uniformsData) {
  const outOfStockItems = [];

  uniformsData?.forEach((uniform) => {
    const sizesOutOfStock = [];

    uniform.sizes?.forEach((sizeItem) => {
      const requestedQty = parseInt(sizeItem.quantity) || 0;
      const availableStock = sizeItem.amountInStock || 0;

      if (requestedQty > availableStock) {
        sizesOutOfStock.push({
          id: sizeItem.id,
          size: sizeItem.size,
          sellPrice: sizeItem.sellPrice,
          requestedQuantity: requestedQty,
          availableStock: availableStock,
          shortage: requestedQty - availableStock,
        });
      }
    });

    // Only add uniform if it has out-of-stock sizes
    if (sizesOutOfStock.length > 0) {
      outOfStockItems.push({
        id: uniform.id,
        name: uniform.name,
        color: uniform.color,
        sizes: sizesOutOfStock,
      });
    }
  });

  return outOfStockItems;
}

export default function SupplieUniform({
  uniformsData = [],
  setUniformsData,
  receiptNumber,
  student = null,
  selectedCurrency,
  onSuccess,
  orderDetails: defaultOrderDetails,
  loadingUniform = true,
  kassa,
}) {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);

  // Core states
  const [selectedUniforms, setSelectedUniforms] = useState({});
  const [allSelectedUniforms, setAllSelectedUniforms] = useState([]);

  // Order states
  const [orderDetails, setOrderDetails] = useState(defaultOrderDetails);

  const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);
  const [showAreYouSure, setAreYouSure] = useState(false);
  const [orderError, setOrderError] = useState(null);

  // Configuration
  const uniformHeaders = [
    { key: "checkbox", label: "choose", type: "checkbox" },
    { key: "size", label: "size", type: "text" },
    { key: "sellPrice", label: "sellPrice", type: "text" },
    { key: "amountInStock", label: "amountInStock", type: "text" },
    { key: "quantity", label: "quantity", type: "input", unit: "piece" },
  ];

  // Print configuration
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 5mm;
        width: 21cm;
        height: 29.7cm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view { display: flex !important; }
      }
    `,
  });

  const validateSelectedUniforms = () => {
    const errors = [];
    allSelectedUniforms.forEach((category) => {
      category.sizes.forEach((size) => {
        const quantity = parseInt(size.quantity) || 0;
        if (quantity <= 0) {
          errors.push(`${category.name} - ${size.size}`);
        }
      });
    });
    return errors;
  };

  const canConfirmOrder = () => {
    if (allSelectedUniforms.length === 0) return false;
    return allSelectedUniforms.every((category) =>
      category.sizes.every((size) => {
        const quantity = parseInt(size.quantity) || 0;
        return quantity > 0;
      })
    );
  };

  const handleConfirmOrder = () => {
    const validationErrors = validateSelectedUniforms();
    if (validationErrors.length > 0) {
      const errorMessage =
        t("pleaseProvideQuantityFor") + ":\n" + validationErrors.join("\n");
      alert(errorMessage);
      return;
    }
    setAreYouSure(true);
  };

  const handleCreateOrder = async () => {
    setConfirmOrderLoading(true);
    setOrderError(null);
    if (!kassa) return;

    try {
      const orderData = {
        receiptNumber,
        date: orderDetails.date || new Date().toISOString(),
        productType: "uniform",
        kassaId: kassa,
        notes: orderDetails.notes,
        items: allSelectedUniforms
          .map((category) =>
            category.sizes.map((size) => ({
              productRulerItemId: size.id,
              quantity: parseInt(size.quantity) || 0,
            }))
          )
          .flat(),
        studentId: student?.id,
        userId: Number(userData.userId),
      };

      const response = await axiosClient.post("/sell", orderData);
      setOrderDetails({
        ...orderDetails,
        createdBy: response.data?.creator?.username,
        companyName: response.data?.creator?.organization?.organizationName,
      });
      setIsPrinting(true);
    } catch (error) {
      console.error("Error creating order:", error);
      setOrderError(t("errorSupplieingUniform"));
    } finally {
      setConfirmOrderLoading(false);
    }
  };

  const handleUniformSelection = (categoryId, sizeId, isSelected) => {
    setOrderError(null);
    setSelectedUniforms((prev) => {
      if (sizeId === null) {
        const { [categoryId]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [sizeId]: isSelected,
        },
      };
    });

    if (sizeId === null) {
      setAllSelectedUniforms((prev) =>
        prev.filter((item) => item.name !== categoryId)
      );
    } else {
      if (isSelected) {
        const category = uniformsData.find((cat) => cat.name === categoryId);
        if (category) {
          const size = category.sizes.find((s) => s.id === sizeId);
          if (size) {
            setAllSelectedUniforms((prev) => {
              const categoryIndex = prev.findIndex(
                (cat) => cat.name === categoryId
              );

              if (categoryIndex >= 0) {
                const sizeExists = prev[categoryIndex].sizes.some(
                  (s) => s.id === sizeId
                );
                if (!sizeExists) {
                  const updatedCategories = [...prev];
                  updatedCategories[categoryIndex] = {
                    ...updatedCategories[categoryIndex],
                    sizes: [
                      ...updatedCategories[categoryIndex].sizes,
                      { ...size },
                    ],
                  };
                  return updatedCategories;
                }
                return prev;
              } else {
                return [
                  ...prev,
                  {
                    id: category.id,
                    name: categoryId,
                    color: category.color,
                    sizes: [{ ...size }],
                  },
                ];
              }
            });
          }
        }
      } else {
        setAllSelectedUniforms((prev) => {
          const updatedCategories = prev.map((cat) => {
            if (cat.name === categoryId) {
              return {
                ...cat,
                sizes: cat.sizes.filter((size) => size.id !== sizeId),
              };
            }
            return cat;
          });
          return updatedCategories.filter((cat) => cat.sizes.length > 0);
        });
      }
    }
  };

  const handleInputChange = (categoryId, sizeId, field, value) => {
    setOrderError(null);
    setAllSelectedUniforms((prev) => {
      return prev.map((category) => {
        if (category.name === categoryId) {
          return {
            ...category,
            sizes: category.sizes.map((size) => {
              if (size.id === sizeId) {
                const updatedSize = { ...size, [field]: value || 0 };
                if (field === "quantity") {
                  updatedSize.totalPrice =
                    (updatedSize.sellPrice || 0) * (value || 0);
                }
                return updatedSize;
              }
              return size;
            }),
          };
        }
        return category;
      });
    });
  };

  const resetToOriginalData = () => {
    setAllSelectedUniforms([]);
    setSelectedUniforms([]);
    onSuccess?.();
    setAreYouSure(false);
    setIsPrinting(false);
  };

  useEffect(() => {
    if (isPrinting) {
      resetToOriginalData();
      handlePrint();
    }
  }, [orderDetails]);

  const outOfStockItems = findOutOfStockUniforms(allSelectedUniforms || []);

  const isConfirmDisabled =
    confirmOrderLoading ||
    !canConfirmOrder() ||
    student === null ||
    outOfStockItems.length > 0;

    useEffect(() => {
      setOrderDetails(defaultOrderDetails)
    }, [defaultOrderDetails])

  return (
    <>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_700">
        <SectionDivider title={t("uniforms")} />

        {loadingUniform && <Loader />}

        {!loadingUniform && (
          <ProductOrderList
            data={uniformsData}
            selectedItems={selectedUniforms}
            onItemSelect={handleUniformSelection}
            currency={selectedCurrency}
            onInputChange={handleInputChange}
            allSelectedItems={allSelectedUniforms}
            headers={uniformHeaders}
            groupByField="name"
            itemsField="sizes"
            itemKeyField="id"
            groupLabelPrefix="uniformName"
          />
        )}

        {allSelectedUniforms.length > 0 && (
          <SectionDivider title={t("selectedUniforms")} />
        )}

        {outOfStockItems.length > 0 && (
          <InformationBox>
            <div>
              <p>
                <strong>{t("outOfStockWarning")}</strong>
              </p>
              <div className="o-column o-row--gap-size_300 u-margin--top_400">
                {outOfStockItems.map((item, index) => (
                  <p key={item.id}>
                    <div>
                      <strong>
                        {item.name} - {item.color}:
                      </strong>
                    </div>
                    {item.sizes.map((size) => (
                      <div key={size.id} className="u-margin--top_100">
                        <strong>{size.size}:</strong> {t("requested")}:{"  "}{" "}
                        {size.requestedQuantity}, {t("available")}:{" "}
                        {size.availableStock}
                      </div>
                    ))}
                  </p>
                ))}
              </div>
            </div>
          </InformationBox>
        )}

        {allSelectedUniforms.length > 0 && !canConfirmOrder() && (
          <InformationBox>
            <p>{t("pleaseProvideQuantityForAllSelectedBooks")}</p>
          </InformationBox>
        )}

        {!loadingUniform && (
          <SelectedProductOrderList
            data={allSelectedUniforms}
            setSelectedItems={setAllSelectedUniforms}
            currency={selectedCurrency}
            selectedItems={selectedUniforms}
            onItemSelect={handleUniformSelection}
            onInputChange={handleInputChange}
            headers={uniformHeaders}
            groupByField="name"
            itemsField="sizes"
            itemKeyField="id"
            groupLabelPrefix="uniformName"
            showDeleteButton={true}
            deleteButtonLabel="delete"
          />
        )}

        {allSelectedUniforms.length > 0 && (
          <CustomInput
            title={`${t("notes")}: `}
            type="textarea"
            onChange={(e) => {
              setOrderDetails((prev) => ({
                ...prev,
                notes: e.target.value,
              }));
            }}
          />
        )}

        {orderError && (
          <div
            className="error-message"
            style={{ color: "red", margin: "10px 0", textAlign: "end" }}
          >
            {orderError}
          </div>
        )}

        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedUniforms.length > 0 && (
            <CustomButton
              title={t("confirm")}
              onClick={handleConfirmOrder}
              icon={saveIcon}
              iconSize={16}
              disabled={isConfirmDisabled}
              style={{
                opacity: isConfirmDisabled ? 0.6 : 1,
                cursor: isConfirmDisabled ? "not-allowed" : "pointer",
              }}
            />
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => {
            setAreYouSure(false);
            setOrderError("");
          }}
          onConfirm={handleCreateOrder}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm
          confirmButtonVariant={null}
          error={orderError}
          loading={confirmOrderLoading}
          confirmIconType="whiteCheck"

        />
      )}

      <PrintableSupplieUniform
        ref={printRef}
        data={allSelectedUniforms}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      />
    </>
  );
}
